import Template from "../../components/Template";
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from "react";
import api from "../../services/api";
import config from "../../config";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { Link } from "react-router-dom";
import { formatCurrencyFromDb, maskCurrency } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";

import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
// Componente TableRow otimizado com memo para evitar re-renders desnecessários
const TableRow = memo(({ doc, columns }) => {
    return (
        <tr key={doc.id}>
            {columns
                .filter((col) => col.visible)
                .map((col) => (
                    <td key={`${doc.id}-${col.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">
                        {col.formatter(doc[col.id], doc)}
                    </td>
                ))}
        </tr>
    );
}, (prevProps, nextProps) => {
    // Custom comparison: só re-renderiza se o doc ou columns mudarem
    return prevProps.doc.id === nextProps.doc.id && 
           prevProps.columns === nextProps.columns;
});

const Obras = () => {
    const navigate = useNavigate();
    const [editais, setEditais] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [clientes, setClientes] = useState([]);
    const { updateMenu } = useNavigation();
    const [obras, setObras] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null); // Estado para o documento selecionado
    const [showDialog, setShowDialog] = useState(false); // Estado para controlar o modal    
    const [selectedPdf, setSelectedPdf] = useState("");
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalRecords, setTotalRecords] = useState(0); // Total de registros    
    const menuRef = useRef(null);
    const menuFiltrarRef = useRef(null);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    
    // Estados para controle de loading e performance
    const [loading, setLoading] = useState(false);
    const renderStartTimeRef = useRef(null);
    const preRenderTimeRef = useRef(null);

    const { filters, setFilters, searchTerm, setSearchTerm, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('obras', {
        status: "",
        codigo: "",
        nomeProjeto: "",
        editalVinculado: "",
        dataInicioInicial: "",
        dataInicioFinal: "",
        dataTerminoInicial: "",
        dataTerminoFinal: "",
        cliente: ""
    });
    const [filteredObras, setFilteredObras] = useState([]);
    const [message, setMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statusRes, editaisRes, clientesRes] = await Promise.all([
                    api.get("/statuses/byModule/nome/obras"),
                    api.get("/editais/todos"),
                    api.get("/clientes/todos")
                ]);
                setStatusList(statusRes.data);
                setEditais(editaisRes.data);
                setClientes(clientesRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchObras(currentPage, pageSize);
    }, [currentPage]);

    useEffect(() => {
        // Sempre que o termo de pesquisa mudar, atualize os documentos filtrados
        if (searchTerm.trim() === "") {
            setFilteredObras(obras); // Se não houver termo de pesquisa, mostre todos os documentos
        } else {
            setFilteredObras(
                obras.filter((doc) =>
                    doc.objeto.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [searchTerm, obras]);

    // Pré-processamento de dados para otimização de renderização
    const preprocessData = useCallback((obras) => {
        return obras.map(obra => {
            // Calcular orçamento pagar (com lógica de orcamentosPrestadores)
            let orcamentoProjetoFormatted = '';
            if (obra?.orcamentosPrestadores && obra?.orcamentosPrestadores.length > 0) {
                const totalOrcamento = obra.orcamentosPrestadores.reduce(
                    (acc, item) => acc + (parseFloat(item.valor) || 0),
                    0
                );
                orcamentoProjetoFormatted = formatCurrencyFromDb(totalOrcamento);
            } else {
                orcamentoProjetoFormatted = formatCurrencyFromDb(obra.orcamentoProjeto) || '';
            }

            return {
                ...obra,
                localizacaoFormatted: obra?.municipio && obra?.uf ? `${obra.municipio}/${obra.uf}` : '',
                dataInicioFormatted: obra.dataInicio ? formatDateWithoutHour(obra.dataInicio) : '',
                dataTerminoFormatted: obra.dataTermino ? formatDateWithoutHour(obra.dataTermino) : '',
                orcamentoProjetoFormatted,
                orcamentoProjetoReceberFormatted: formatCurrencyFromDb(obra.orcamentoProjetoReceber) || '',
                gastoFormatted: formatCurrencyFromDb(obra.gasto) || '',
                saldoFormatted: formatCurrencyFromDb(obra.saldoMedir) || '',
                contratoNumero: obra.contrato?.numeroContrato || '',
                statusNome: obra.status?.nome || ''
            };
        });
    }, []);

    // Filtros efetivamente aplicados pelo botao "Filtrar". Sao reenviados em toda
    // troca de pagina e de "resultados por pagina": antes a paginacao refazia a
    // busca sem filtro nenhum e a lista voltava silenciosamente ao conjunto completo.
    const appliedFiltersRef = useRef({});

    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchObras = async (page, size) => {
        try {
            setLoading(true);
            renderStartTimeRef.current = performance.now();
            
            console.log('🚀 [Obras] Iniciando busca...', { page, size });
            console.time('⏱️ [Obras] Tempo Total');
            console.time('📡 [Obras] API Request');
            
            const params = new URLSearchParams({ page, pageSize: size });
            Object.entries(appliedFiltersRef.current).forEach(([chave, valor]) => {
                if (valor !== undefined && valor !== null && valor !== "") params.set(chave, valor);
            });
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/obras?${params.toString()}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiTime = performance.now() - renderStartTimeRef.current;
            console.timeEnd('📡 [Obras] API Request');
            console.log(`✅ [Obras] API retornou ${response.data.records.length} obras`);
            
            console.time('🔄 [Obras] Pré-processamento');
            const preprocessStart = performance.now();
            const processedData = preprocessData(response.data.records);
            const preprocessTime = performance.now() - preprocessStart;
            console.timeEnd('🔄 [Obras] Pré-processamento');
            console.log(`✅ [Obras] ${processedData.length} registros pré-processados em ${preprocessTime.toFixed(2)}ms`);
            
            console.time('💾 [Obras] State Update');
            const stateUpdateStart = performance.now();
            setObras(processedData);
            setTotalRecords(response.data.total);
            setPageSize(size);
            const stateUpdateTime = performance.now() - stateUpdateStart;
            console.timeEnd('💾 [Obras] State Update');
            
            preRenderTimeRef.current = performance.now();
            
            // Aguardar 100ms para garantir que o DOM seja renderizado
            setTimeout(() => {
                requestAnimationFrame(() => {
                    const renderTime = performance.now() - preRenderTimeRef.current;
                    const totalTime = performance.now() - renderStartTimeRef.current;
                    
                    console.timeEnd('⏱️ [Obras] Tempo Total');
                    console.log('📊 [Obras] Análise de Performance:');
                    console.log(`  📡 API: ${apiTime.toFixed(2)}ms (${((apiTime / totalTime) * 100).toFixed(1)}%)`);
                    console.log(`  🔄 Pré-processamento: ${preprocessTime.toFixed(2)}ms (${((preprocessTime / totalTime) * 100).toFixed(1)}%)`);
                    console.log(`  💾 State Update: ${stateUpdateTime.toFixed(3)}ms (~0%)`);
                    console.log(`  🎨 Renderização Pura: ${renderTime.toFixed(2)}ms (${((renderTime / totalTime) * 100).toFixed(1)}%)`);
                    console.log(`  ⏱️ TOTAL: ${totalTime.toFixed(2)}ms`);
                    
                    if (renderTime > 5000) {
                        console.warn('⚠️ [Obras] Renderização MUITO LENTA (>5s)! Considere reduzir pageSize ou implementar virtualização.');
                    } else if (renderTime > 2000) {
                        console.warn('⚠️ [Obras] Renderização lenta (>2s). Considere reduzir pageSize.');
                    }
                    
                    setLoading(false);
                });
            }, 100);
        } catch (error) {
            console.error("❌ [Obras] Erro ao carregar dados:", error);
            setLoading(false);
        }
    };

    const fetchFilteredObras = async (query) => {
        try {
            setLoading(true);
            renderStartTimeRef.current = performance.now();
            
            console.log('🔍 [Obras] Aplicando filtros...');
            console.time('⏱️ [Obras] Filtro Total');
            console.time('📡 [Obras] API Filtro');
            
            const params = new URLSearchParams(query).toString();
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/obras?${params}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            
            const apiTime = performance.now() - renderStartTimeRef.current;
            console.timeEnd('📡 [Obras] API Filtro');
            console.log(`✅ [Obras] Filtro retornou ${response.data.records.length} obras`);
            
            console.time('🔄 [Obras] Pré-processamento Filtro');
            const preprocessStart = performance.now();
            const processedData = preprocessData(response.data.records);
            const preprocessTime = performance.now() - preprocessStart;
            console.timeEnd('🔄 [Obras] Pré-processamento Filtro');
            
            console.time('💾 [Obras] State Update Filtro');
            const stateUpdateStart = performance.now();
            setObras(processedData);
            setTotalRecords(response.data.total);
            setCurrentPage(1);
            const stateUpdateTime = performance.now() - stateUpdateStart;
            console.timeEnd('💾 [Obras] State Update Filtro');
            
            preRenderTimeRef.current = performance.now();
            
            setTimeout(() => {
                requestAnimationFrame(() => {
                    const renderTime = performance.now() - preRenderTimeRef.current;
                    const totalTime = performance.now() - renderStartTimeRef.current;
                    
                    console.timeEnd('⏱️ [Obras] Filtro Total');
                    console.log('📊 [Obras] Análise Filtro:');
                    console.log(`  📡 API: ${apiTime.toFixed(2)}ms (${((apiTime / totalTime) * 100).toFixed(1)}%)`);
                    console.log(`  🔄 Pré-processamento: ${preprocessTime.toFixed(2)}ms (${((preprocessTime / totalTime) * 100).toFixed(1)}%)`);
                    console.log(`  🎨 Renderização: ${renderTime.toFixed(2)}ms (${((renderTime / totalTime) * 100).toFixed(1)}%)`);
                    console.log(`  ⏱️ TOTAL: ${totalTime.toFixed(2)}ms`);
                    
                    setLoading(false);
                });
            }, 100);
        } catch (error) {
            console.error("❌ [Obras] Erro ao aplicar filtros:", error);
            setLoading(false);
        }
    };

    const handleNavigateCadastrar = async (e) => {
        e.preventDefault();
        try {
            navigate('/obras/cadastrar');
        } catch (err) {
            console.error('Obras Error:', err);
            alert(err.response?.data?.msg || 'Erro ao ir para o cadastro');
        }
    };

    const handleCloseDialog = () => {
        setSelectedDoc(null);
        setShowDialog(false);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    const pageWindowSize = 5;
    // Mantem a janela cheia tambem nas ultimas paginas (antes ela encolhia).
    const startPage = Math.max(1, Math.min(currentPage - Math.floor(pageWindowSize / 2), totalPages - pageWindowSize + 1));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);

    const pageNumbers = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
    );

    const [columns, setColumns] = useState([
        {
            id: "id",
            label: "ID",
            visible: true,
            formatter: (value) => (
                <Link to={`/obras/editar/${value}`} className="text-stone-600 hover:underline">
                    {value}
                </Link>
            ),
        },
        {
            id: "nomeProjeto",
            label: "Obra",
            visible: true,
            formatter: (value) =>
                value ?? "",
        },
        {
            id: "municipio",
            label: "Localização",
            visible: true,
            formatter: (value, doc) => doc.localizacaoFormatted,
        },

        {
            id: "dataInicio",
            label: "Data de Início",
            visible: true,
            formatter: (value, doc) => doc.dataInicioFormatted,
        },
        {
            id: "dataTermino",
            label: "Data de Término",
            visible: true,
            formatter: (value, doc) => doc.dataTerminoFormatted,
        },
        {
            id: "orcamentoProjetoReceber",
            label: "Orçamento (Receber)",
            visible: true,
            formatter: (value, doc) => doc.orcamentoProjetoReceberFormatted,
        },

        {
            id: "gasto",
            label: "Gasto",
            visible: true,
            formatter: (value, doc) => doc.gastoFormatted,
        },

        {
            id: "saldo",
            label: "Saldo",
            visible: true,
            formatter: (value, doc) => doc.saldoFormatted,
        },
        {
            id: "contrato",
            label: "Contrato",
            visible: true,
            formatter: (value, doc) => doc.contratoNumero,
        },
        {
            id: "status",
            label: "Status",
            visible: true,
            formatter: (value, doc) => {
                const nome = doc.statusNome || '';
                const isConcluido = nome.trim().toLowerCase() === 'concluído'
                    || nome.trim().toLowerCase() === 'concluido';
                if (isConcluido) {
                    return (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-300">
                            {nome}
                        </span>
                    );
                }
                return nome;
            },
        },

    ]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuFiltrarOpen, setMenuFiltrarOpen] = useState(false);

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    // Função para fechar o menu ao clicar fora
    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);

        };
    }, [menuOpen]);

    const handleMenuClick = (event) => {
        event.stopPropagation();
    };

    const applyFilters = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };

        fetchFilteredObras(query);
    };

    const clearFilters = () => {
        clearPersistedState();
        appliedFiltersRef.current = {};
        setSearchTerm("");
        setCurrentPage(1);

        setFilters({
            status: "",
            codigo: "",
            nomeProjeto: "",
            editalVinculado: "",
            dataInicioInicial: "",
            dataInicioFinal: "",
            dataTerminoInicial: "",
            dataTerminoFinal: "",
            cliente: ""
        });
        fetchObras(1, pageSize); // Restaura os dados padrão
    };

    const handleSearch = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1); // Reinicia para a primeira página
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };
        fetchFilteredObras(query); // Executa a busca com o termo atual
    };

    // Função para exportar a tabela
    const exportToExcel = () => {
        if (obras.length === 0) {
            setMessage("Não há dados para exportar!");
            setShowSuccessDialog(true);
            return;
        }

        // Define quais colunas são monetárias
        const monetaryColumns = ['orcamentoProjeto', 'orcamentoProjetoReceber']; // substitua pelos IDs reais        

        // Mapeia os dados da tabela para um formato plano
        const dataToExport = obras.map((doc) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible) {
                    // Formatters de coluna que devolvem JSX (links, botoes, checkboxes)
                    // viravam "[object Object]" na planilha: nesses casos exporta o valor bruto.
                    let value = col.formatter ? col.formatter(doc[col.id], doc) : doc[col.id];
                    if (value && typeof value === 'object') value = doc[col.id];
                    // Remove formatação monetária (p.ex. 'R$', '.', ',') e converte para número
                    if (monetaryColumns.includes(col.id)) {
                        value = Number(String(value)
                            .replace(/[R$\s.]/g, '')
                            .replace(',', '.')
                        );
                    }

                    row[col.label] = value;
                }
            });
            return row;
        });

        // Cria a planilha do Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        // Formata células monetárias explicitamente como números no Excel
        const columnLabels = columns.filter(col => col.visible).map(col => col.label);
        monetaryColumns.forEach((colId) => {
            const colIndex = columnLabels.indexOf(columns.find(c => c.id === colId)?.label);
            if (colIndex >= 0) {
                const columnLetter = XLSX.utils.encode_col(colIndex);
                const range = XLSX.utils.decode_range(worksheet['!ref']);

                for (let i = range.s.r + 1; i <= range.e.r; i++) {
                    const cellAddress = `${columnLetter}${i + 1}`;
                    if (worksheet[cellAddress]) {
                        worksheet[cellAddress].t = 'n';
                        worksheet[cellAddress].z = '#,##0.00';
                    }
                }
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Obras");

        // Exporta o arquivo
        XLSX.writeFile(workbook, "obras.xlsx");
    };

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium font-['Inter']">Obras</div>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Ver</div>
                        <select
                            className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            value={pageSize}
                            disabled={loading}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10);
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                                fetchObras(1, newPageSize);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={150}>150</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                            <option value={2000}>2000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Resultados</div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex" >
                                <img className="w-6 h-6 relative " src="images/icon/columns.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Colunas</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {menuOpen === "colunas" && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
                                    style={{
                                        zIndex: 9999, // Garante que está acima de outros elementos
                                    }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div
                                                key={col.id}
                                                className="flex items-center gap-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() =>
                                                        toggleColumnVisibility(col.id)
                                                    }
                                                />
                                                <label
                                                    htmlFor={col.id}
                                                    className="text-sm text-gray-700"
                                                >
                                                    {col.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")} >
                            <div className="justify-start items-center gap-2 flex">
                                <img className="w-8 h-8 px-0.5 py-[3px] justify-center items-center flex" src="images/icon/filter.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Filtrar</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {/* Menu Suspenso */}
                            {menuOpen === "filtro" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="self-stretch justify-start items-center gap-2.5 inline-flex">
                                            <div className="grow shrink basis-0 text-stone-800 text-xl font-medium">
                                                Filtrar
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Cliente
                                            </div>
                                            <select
                                                name="cliente"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.cliente}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, cliente: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {clientes.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                ID da Obra
                                            </div>
                                            <input
                                                type="text"
                                                name="codigo"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="ID da Obra"
                                                value={filters.codigo}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, codigo: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Nome da Obra
                                            </div>
                                            <input
                                                type="text"
                                                name="nomeProjeto"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Nome da Obra"
                                                value={filters.nomeProjeto}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, nomeProjeto: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Edital Vinculado
                                            </div>
                                            <select
                                                name="editalVinculado"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.editalVinculado}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, editalVinculado: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {editais.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.numeroEdital}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Status
                                            </div>
                                            <select
                                                name="status"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.status}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, status: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {statusList.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Início
                                            </div>
                                            <div className="flex gap-2">

                                                <input
                                                    type="date"
                                                    name="dataInicioInicial"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataInicioInicial}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataInicioInicial: e.target.value })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="dataInicioFinal"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataInicioFinal}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataInicioFinal: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Fim
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    name="dataTerminoInicial"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataTerminoInicial}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataTerminoInicial: e.target.value })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="dataTerminoFinal"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataTerminoFinal}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataTerminoFinal: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="self-stretch flex items-center justify-between gap-4 mt-4">
                                            <button
                                                className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 justify-center items-center gap-2 flex"
                                                onClick={clearFilters}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/eraser.png" />
                                                <div className="text-stone-500 text-sm font-medium font-['Inter'] leading-normal" >Limpar</div>
                                            </button>
                                            <button
                                                className="px-4 py-2 w-full bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                                onClick={applyFilters}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/filter-white.png" />
                                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Filtrar</div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="justify-start items-center gap-2 flex">
                            {!isSearchActive ? (
                                // Conteúdo original da div "Pesquisar"
                                <div
                                    className="justify-start items-center gap-2 flex cursor-pointer"
                                    onClick={() => setIsSearchActive(true)} // Ativar o estado de pesquisa
                                >
                                    <img className="w-10 h-10 relative" src="images/icon/search.png" alt="Ícone Pesquisar" />
                                    <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                </div>
                            ) : (
                                // Conteúdo alternativo ao ativar o estado de pesquisa
                                <div className="h-10 justify-start items-center gap-2 inline-flex">
                                    <div className="h-9 px-3 py-2 bg-white rounded-md border border-slate-300 justify-start items-center flex">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar pelo ID"
                                            className="w-full text-slate-400 text-base font-normal font-['Inter'] leading-tight focus:outline-none"
                                            value={filters.codigo}
                                            onChange={(e) =>
                                                setFilters({ ...filters, codigo: e.target.value })
                                            }
                                        />
                                        <img className="w-6 h-6 p-[3.33px] justify-center items-center flex cursor-pointer" src="images/icon/close.png" onClick={() => {
                                            setIsSearchActive(false);
                                        }} />
                                    </div>
                                    <button
                                        className="px-4 py-2 bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                        onClick={handleSearch}
                                    >
                                        <img className="w-4 h-4 relative" src="images/icon/search-white.png" alt="Ícone Pesquisar" />
                                        <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="self-stretch flex justify-between gap-4">
                        {usePermission('EXPORTAR_OBRA') && (
                            <button type='button' className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer" onClick={exportToExcel}>
                                <img className="w-4 h-4" src="images/icon/upload.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Exportar</div>
                            </button>
                        )}
                        {usePermission('CRIAR_OBRA') && (
                            <button 
                                type='button' 
                                className="px-4 py-2 bg-stone-600 text-white rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                                onClick={handleNavigateCadastrar}
                                disabled={loading}
                            >
                                <img className="w-4 h-4" src="images/icon/plus-circle.png" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Cadastrar</div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Spinner de carregamento */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-stone-600"></div>
                        <p className="mt-4 text-stone-600 text-lg">
                            {pageSize > 100 
                                ? `Carregando muitos registros (${pageSize}), aguarde...` 
                                : 'Carregando obras...'}
                        </p>
                    </div>
                )}

                {/* Tabela - só exibe quando não está carregando */}
                {!loading && (
                    <div className="overflow-x-auto md:overflow-x-visible">
                        <table className="min-w-full bg-white border-collapse border border-gray-200">
                            <thead className="bg-stone-200">
                                <tr>
                                    {columns
                                        .filter((col) => col.visible)
                                        .map((col) => (
                                            <th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">{col.label}</th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredObras.map((doc) => (
                                    <TableRow key={doc.id} doc={doc} columns={columns} />
                                ))}
                        {totalRecords === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.filter((col) => col.visible).length || 1}
                                    className="px-4 py-6 border border-slate-200 text-center text-slate-500 text-base"
                                >
                                    Nenhum registro encontrado.
                                </td>
                            </tr>
                        )}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-slate-500 text-base font-normal font-['Inter'] leading-[29.60px]">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords}{" "} registros.
                    </div>
                    <div className="justify-start items-start gap-4 flex">
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${
                                currentPage === 1 || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                            }`}
                            disabled={currentPage === 1 || loading}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-left.png" />
                        </button>

                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded justify-center items-center flex text-base font-semibold font-['Inter'] ${
                                    page === currentPage
                                        ? "bg-stone-600 text-white"
                                        : "bg-gray-200 text-gray-600"
                                } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                                disabled={loading}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button 
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${
                                currentPage >= totalPages || totalPages === 0 || loading ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            disabled={currentPage >= totalPages || totalPages === 0 || loading}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-right.png" />
                        </button>
                    </div>
                </div>
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                        <img className="w-6 h-6 relative  overflow-hidden" src="images/icon/download.png" />
                                    </a>
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                </div>
                                <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="images/icon/x.png" onClick={handleCloseDialog} />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    style={{ border: 'none' }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    )
}
export default Obras;